import { UserProfile } from "@/gen/ts/v1/user/user_service_pb";
import { analytics } from "@/lib/analytics/ga4";
import { createLogger } from "@/lib/observability/logger";
import {
  AuthError,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
} from "firebase/auth";
import { ensureUserProfile, getUserById } from "../../users/api/users.client";
import { computeAuthRedirectUrl } from "../util/computeAuthRedirect";
import { sanitizeFirebaseError } from "../util/sanitizeFirebaseError";
import { refreshSession } from "./refresh.client";

export type SignInResult =
  | {
      finalRedirectUrl: string;
      userProfile: UserProfile;
    }
  | never;

export async function signIn({
  email,
  password,
  redirectUrl,
}: {
  email: string;
  password: string;
  redirectUrl?: string;
}): Promise<SignInResult> {
  const logger = createLogger("signIn");
  const auth = getAuth();

  // Step 1: Authenticate with Firebase
  try {
    await signInWithEmailAndPassword(auth, email, password);
    analytics.event({
      action: "signIn",
      category: "auth",
      custom_parameters: {
        method: "email_password",
        status: "success",
      },
    });
  } catch (error) {
    const authError = error as AuthError;
    const { sanitizedMessage, loggedMessage, errorCode, severity } =
      sanitizeFirebaseError(authError);

    logger.error(loggedMessage, error as Error, {
      email,
      step: "firebase_auth",
      errorCode,
    });

    analytics.error(error as Error, "email_password_signIn", severity);

    throw new Error(sanitizedMessage);
  }

  // Step 2: Fetch user profile from database
  // If this fails, we have a Firebase user but no database record - critical error
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new Error("Firebase user ID not available after successful signIn");
    }

    const getUserResult = await getUserById({
      identifier: { case: "userId", value: userId },
    });
    if (!getUserResult.userProfile) {
      throw new Error("User profile not found in database");
    }

    refreshSession();
    return {
      finalRedirectUrl: computeAuthRedirectUrl({
        wasCreated: false,
        generatedUsername: null,
        userProfile: getUserResult.userProfile,
        redirectUrl,
      }),
      userProfile: getUserResult.userProfile,
    };
  } catch (error) {
    // Critical: Firebase auth succeeded but profile fetch failed
    // Sign out to maintain consistency between Firebase and database state
    logger.error(
      "Profile fetch failed after successful Firebase auth",
      error as Error,
      {
        email,
        step: "profile_fetch",
        firebaseUid: auth.currentUser?.uid,
      },
    );

    analytics.error(error as Error, "profile_fetch_after_signIn", "high");

    // Clean up Firebase session to avoid inconsistent state
    try {
      await auth.signOut();
    } catch (signOutError) {
      logger.error(
        "Failed to sign out after profile fetch error",
        signOutError as Error,
        {
          originalError: (error as Error).message,
        },
      );
    }

    throw new Error(
      "Unable to load your account. Please try again or contact support.",
    );
  }
}

export type SignInWithGoogleResult =
  | {
      firebaseUser: User | null;
      wasCreated: boolean;
      generatedUsername: string | null;
      userProfile: UserProfile | null;
      finalRedirectUrl: string;
    }
  | never;

export async function signInWithGoogle({
  redirectUrl,
}: {
  redirectUrl?: string;
}): Promise<SignInWithGoogleResult> {
  const logger = createLogger("signInWithGoogle");
  const auth = getAuth();
  const provider = new GoogleAuthProvider();
  let idToken: string | null = null;

  // Step 1: Authenticate with Google via Firebase
  try {
    const result = await signInWithPopup(auth, provider);

    // Get Firebase ID token from the authenticated user (not Google OAuth token)
    // The Firebase ID token has the correct audience claim for our Firebase project
    idToken = await result.user.getIdToken();
    if (!idToken) {
      throw new Error("Firebase ID token not available after Google sign-in");
    }

    analytics.event({
      action: "signIn",
      category: "auth",
      custom_parameters: {
        method: "google_popup",
        status: "success",
      },
    });
  } catch (error) {
    const authError = error as AuthError;
    const { sanitizedMessage, loggedMessage, errorCode, severity } =
      sanitizeFirebaseError(authError);

    logger.error(loggedMessage, error as Error, {
      step: "firebase_google_auth",
      errorCode,
    });

    analytics.error(error as Error, "google_signIn", severity);

    throw new Error(sanitizedMessage);
  }

  // Step 2: Ensure user profile exists in database (create if new user)
  // If this fails, we have a Firebase user but no database record - critical error
  try {
    const { userProfile, wasCreated, generatedUsername } =
      await ensureUserProfile({
        authMethod: {
          value: idToken,
          case: "idToken",
        },
      });

    if (!userProfile) {
      throw new Error("User profile not returned from ensureUserProfile");
    }

    if (wasCreated) {
      analytics.onboardingStep(
        "signup",
        1,
        "consumer",
        "google_popup",
        "success",
      );
    }

    refreshSession();

    return {
      firebaseUser: auth.currentUser ?? null,
      userProfile,
      wasCreated,
      generatedUsername: generatedUsername ?? null,
      finalRedirectUrl: computeAuthRedirectUrl({
        wasCreated,
        generatedUsername: generatedUsername ?? null,
        userProfile,
        redirectUrl: redirectUrl ?? undefined,
      }),
    };
  } catch (error) {
    // Critical: Firebase auth succeeded but profile ensure/fetch failed
    // Sign out to maintain consistency between Firebase and database state
    logger.error(
      "Profile ensure failed after successful Google auth",
      error as Error,
      {
        step: "profile_ensure",
        firebaseUid: auth.currentUser?.uid,
      },
    );

    analytics.error(
      error as Error,
      "profile_ensure_after_google_signIn",
      "high",
    );

    // Clean up Firebase session to avoid inconsistent state
    try {
      await auth.signOut();
    } catch (signOutError) {
      logger.error(
        "Failed to sign out after profile ensure error",
        signOutError as Error,
        {
          originalError: (error as Error).message,
        },
      );
    }

    throw new Error(
      "Unable to complete sign-in. Please try again or contact support.",
    );
  }
}

export async function signInWithMicrosoft() {
  throw new Error("Microsoft signIn is not implemented");
}
